/**
 * Workspace Draft Service – Phase 3C.
 *
 * Manages debounced draft persistence for workspace-sourced layouts.
 *
 * Design:
 *  - Singleton debounce timer (one workspace active at a time)
 *  - 1500ms debounce: frequent mutations (drag, field edits) batch into one save
 *  - flushDraftSave(): force-save immediately (e.g., on page unload / Done button)
 *  - cancelDraftSave(): discard pending save (e.g., on workspace unmount)
 *  - Non-fatal: a failed save is logged but never surfaced as an error to the user
 *    The next mutation will re-schedule a save automatically.
 *
 * Published versions are NEVER called from here.
 * Only PATCH /api/v2/workspaces/{id}/draft is used.
 */
import { updateWorkspaceDraft } from '../../api/workspaceApi';

const DRAFT_SAVE_DEBOUNCE_MS = 1500;

let _saveTimer      = null;
let _pendingSchema  = null;
let _pendingCtx     = null;
let _isSaving       = false;
let _waiters        = [];
let _statusListeners = new Set();
const _draftRevisions = new Map();

export const rememberDraftRevision = (workspaceId, revision) => {
  if (!workspaceId) return;
  const number = Number(revision);
  _draftRevisions.set(workspaceId, Number.isInteger(number) && number >= 0 ? number : 0);
};

export const draftRevisionFor = (workspaceId) => _draftRevisions.get(workspaceId) ?? 0;

/**
 * Subscribe to draft save status changes.
 * Status values: 'idle' | 'pending' | 'saving' | 'saved' | 'error'
 * @param {(status: string) => void} listener
 * @returns {() => void} unsubscribe function
 */
export const subscribeSaveStatus = (listener) => {
  _statusListeners.add(listener);
  return () => _statusListeners.delete(listener);
};

const _emitStatus = (status) => {
  _statusListeners.forEach((l) => {
    try { l(status); } catch (_) {}
  });
};

/**
 * Build a layoutSchema object from a flat layout snapshot.
 * Only the fields that belong in Firestore workspaceVersions.layoutSchema are included.
 */
const buildLayoutSchema = (snapshot) => ({
  icons:              Array.isArray(snapshot?.icons) ? snapshot.icons : [],
  theme:              snapshot?.theme              || 'system',
  gridColumns:        snapshot?.gridColumns        || 4,
  gridRows:           snapshot?.gridRows           || 6,
  iconSize:           snapshot?.iconSize           || 'medium',
  gap:                snapshot?.gap                ?? 2,
  showGrid:           snapshot?.showGrid           ?? false,
  backgroundImageUrl: snapshot?.backgroundImageUrl || '',
});

/**
 * Execute a single draft save immediately.
 * @private
 */
const _executeSave = async (runtimeContext, layoutSchema, { strict = false } = {}) => {
  if (!runtimeContext?.workspaceId) {
    console.warn('[WorkspaceDraftService] save called with no workspaceId – skipped');
    return;
  }
  _isSaving = true;
  _emitStatus('saving');
  const waiters = _waiters;
  _waiters = [];
  try {
    const workspaceId = runtimeContext.workspaceId;
    const seeded = Number(runtimeContext?.draftRevision);
    const baseRevision = _draftRevisions.has(workspaceId)
      ? _draftRevisions.get(workspaceId)
      : (Number.isInteger(seeded) && seeded >= 0 ? seeded : 0);
    const response = await updateWorkspaceDraft(workspaceId, layoutSchema, baseRevision);
    const saved = response?.data || response;
    const nextRevision = Number(saved?.revision);
    if (Number.isInteger(nextRevision) && nextRevision >= 0) {
      rememberDraftRevision(workspaceId, nextRevision);
    }
    _emitStatus('saved');
    waiters.forEach((waiter) => waiter.resolve(saved));
    return saved;
  } catch (err) {
    const conflict = String(err?.message || '').includes('layout_conflict') || String(err?.message || '').includes('(409)');
    _emitStatus(conflict ? 'conflict' : 'error');
    waiters.forEach((waiter) => waiter.reject(err));
    if (strict || conflict) throw err;
    console.warn('[WorkspaceDraftService] Draft save failed', {
      workspaceId: runtimeContext?.workspaceId,
      error:       err?.message,
    });
    throw err;
  } finally {
    _isSaving = false;
  }
};

/**
 * Schedule a debounced draft save.
 * Replaces any pending save with the latest snapshot.
 *
 * @param {import('./workspaceResolverService').RuntimeContext} runtimeContext
 * @param {object} layoutSnapshot – flat layout object { icons, theme, … }
 */
export const scheduleDraftSave = (runtimeContext, layoutSnapshot) => {
  _pendingSchema = buildLayoutSchema(layoutSnapshot);
  _pendingCtx    = runtimeContext;
  _emitStatus('pending');
  const promise = new Promise((resolve, reject) => {
    _waiters.push({ resolve, reject });
  });

  clearTimeout(_saveTimer);
  _saveTimer = setTimeout(async () => {
    if (!_pendingSchema || !_pendingCtx) return;
    const schema = _pendingSchema;
    const ctx    = _pendingCtx;
    _pendingSchema = null;
    _pendingCtx    = null;
    try {
      await _executeSave(ctx, schema);
    } catch {
      // Waiters were already rejected. The editor keeps the local draft.
    }
  }, DRAFT_SAVE_DEBOUNCE_MS);
  return promise;
};

/**
 * Flush any pending save immediately (bypass debounce).
 * Safe to call when no save is pending.
 *
 * @param {import('./workspaceResolverService').RuntimeContext} [runtimeContext]
 * @param {object} [layoutSnapshot] – if provided, overrides pending snapshot
 */
export const flushDraftSave = async (runtimeContext, layoutSnapshot, options = {}) => {
  clearTimeout(_saveTimer);

  const schema = layoutSnapshot
    ? buildLayoutSchema(layoutSnapshot)
    : _pendingSchema;
  const ctx = runtimeContext ?? _pendingCtx;

  const started = Date.now();
  while (_isSaving && Date.now() - started < 8000) {
    await new Promise((resolve) => setTimeout(resolve, 40));
  }

  _pendingSchema = null;
  _pendingCtx = null;

  if (!ctx || !schema) return null;

  return _executeSave(ctx, schema, options);
};

/**
 * Cancel any pending save and clear state.
 * Call when the workspace is unloaded or the user discards changes.
 */
export const cancelDraftSave = () => {
  clearTimeout(_saveTimer);
  _pendingSchema = null;
  _pendingCtx    = null;
};
